class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Manual "Refresh" link (delegated: survives re-renders)
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-refresh]')) {
        event.preventDefault();
        this.refresh();
      }
    });
  }

  /* Re-fetch the drawer + cart bubble from Shopify and swap them in */
  async refresh() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    const link = this.querySelector('[data-cart-refresh]');
    if (link) {
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Refreshing…';
    }
    try {
      const url = `${window.location.pathname}?sections=cart-drawer,cart-icon-bubble`;
      const response = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      this.renderSections(await response.json());
    } catch (error) {
      console.error('Cart refresh failed', error);
      if (link) {
        link.removeAttribute('aria-busy');
        link.textContent = 'Refresh';
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /* Swap in freshly rendered sections ({ 'cart-drawer': html, 'cart-icon-bubble': html }) */
  renderSections(sections) {
    if (!sections) return;

    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const doc = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const newInner = doc.querySelector('.drawer__inner');
      const currentInner = this.querySelector('.drawer__inner');
      if (newInner && currentInner) currentInner.innerHTML = newInner.innerHTML;

      const newDrawer = doc.querySelector('cart-drawer');
      if (newDrawer) this.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
    }

    const bubbleHTML = sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHTML && bubble) {
      const section = new DOMParser().parseFromString(bubbleHTML, 'text/html').querySelector('.shopify-section');
      if (section) bubble.innerHTML = section.innerHTML;
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.querySelector('html').classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.querySelector('html').classList.remove('overflow-hidden');
    this.querySelector('cart-drawer-items').classList.remove('activeNote');
    this.querySelector('cart-note').classList.remove('active');
    this.querySelector('.drawer__footer').querySelector('.cart-drawer__footer').classList.remove('activeNote');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ------------------------------------------------------------------
   Variant switcher inside the cart drawer
   - Normal items: one request (/cart/update.js) swaps old line -> new variant
     and returns the re-rendered drawer in the same response.
   - Items with line properties (e.g. PWP add-ons) or subscriptions:
     add the new variant WITH the same properties, then remove the old line,
     so add-on/discount data is never lost.
------------------------------------------------------------------- */
class CartVariantPicker extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    try {
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    } catch (e) {
      this.variants = [];
    }
    this.selects = Array.from(this.querySelectorAll('select[data-option-index]'));

    // Make sure the current variant is selected (robust to option-value object comparisons in Liquid)
    try {
      const current = JSON.parse(this.dataset.current || '[]');
      this.selects.forEach((select, i) => {
        if (current[i] != null) select.value = current[i];
      });
    } catch (e) {}
    this.previousValues = this.selectedOptions();
    this.labelAvailability();

    this.selects.forEach((select, i) => {
      select.addEventListener('change', (event) => {
        // Stop the theme's quantity handler (on <cart-drawer-items>) from treating this as a qty change
        event.stopPropagation();
        this.onOptionChange(i);
      });
    });
  }

  selectedOptions() {
    return this.selects.map((s) => s.value);
  }

  findVariant(options) {
    return this.variants.find((v) => v.options.every((value, i) => value === options[i]));
  }

  /* Add "– Sold out" to values that can't be combined with the other current selections */
  labelAvailability() {
    const selected = this.selectedOptions();
    this.selects.forEach((select, i) => {
      Array.from(select.options).forEach((opt) => {
        const test = selected.slice();
        test[i] = opt.value;
        const variant = this.findVariant(test);
        const isCurrent = variant && String(variant.id) === this.dataset.variantId;
        const anyAvailable = this.variants.some((v) => v.available && v.options[i] === opt.value);
        let suffix = '';
        if (!isCurrent && !anyAvailable) suffix = ' – Sold out';
        opt.textContent = opt.value + suffix;
        opt.disabled = !isCurrent && !anyAvailable;
      });
    });
  }

  onOptionChange(changedIndex) {
    if (this.busy) return;
    const options = this.selectedOptions();
    let variant = this.findVariant(options);

    // Exact combination unavailable? Pick the first in-stock variant with the value they just chose.
    if (!variant || !variant.available) {
      const chosen = options[changedIndex];
      variant =
        this.variants.find(
          (v) => v.available && v.options[changedIndex] === chosen &&
            v.options.every((val, i) => i === changedIndex || val === options[i])
        ) || this.variants.find((v) => v.available && v.options[changedIndex] === chosen);
      if (variant) this.selects.forEach((s, i) => (s.value = variant.options[i]));
    }

    if (!variant) {
      this.showError('That option is sold out.');
      this.revert();
      return;
    }
    if (String(variant.id) === this.dataset.variantId) return;

    this.swap(variant.id);
  }

  revert() {
    this.selects.forEach((s, i) => (s.value = this.previousValues[i]));
  }

  setLoading(isLoading) {
    this.busy = isLoading;
    this.classList.toggle('is-loading', isLoading);
    const row = document.getElementById(`CartDrawer-Item-${this.dataset.index}`);
    const overlay = row && row.querySelector('.loading-overlay');
    if (overlay) overlay.classList.toggle('hidden', !isLoading);
  }

  showError(message) {
    const el = document.querySelector(`#CartDrawer-LineItemError-${this.dataset.index} .cart-item__error-text`);
    if (el) el.textContent = message || '';
  }

  route(name, fallback) {
    return (window.routes && window.routes[name]) || fallback;
  }

  async postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not update this item.');
    }
    return data;
  }

  async swap(newVariantId) {
    this.setLoading(true);
    this.showError('');

    const drawer = document.querySelector('cart-drawer');
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;
    const quantity = parseInt(this.dataset.quantity, 10) || 1;
    const lineKey = this.dataset.lineKey;

    try {
      let state;

      if (this.dataset.keepProperties === 'true') {
        const item = { id: newVariantId, quantity };
        try {
          const props = JSON.parse(this.dataset.properties || '{}');
          if (props && Object.keys(props).length) item.properties = props;
        } catch (e) {}
        if (this.dataset.sellingPlan) item.selling_plan = Number(this.dataset.sellingPlan);

        await this.postJSON(`${this.route('cart_add_url', '/cart/add')}.js`, { items: [item] });
        state = await this.postJSON(`${this.route('cart_change_url', '/cart/change')}.js`, {
          id: lineKey,
          quantity: 0,
          sections,
          sections_url: sectionsUrl,
        });
      } else {
        // If the new variant is already in the cart, merge quantities instead of overwriting
        let existing = 0;
        document
          .querySelectorAll(`#CartDrawer .quantity__input[data-quantity-variant-id="${newVariantId}"]`)
          .forEach((input) => (existing += parseInt(input.value, 10) || 0));

        const updates = {};
        updates[lineKey] = 0;
        updates[newVariantId] = existing + quantity;

        state = await this.postJSON(`${this.route('cart_update_url', '/cart/update')}.js`, {
          updates,
          sections,
          sections_url: sectionsUrl,
        });
      }

      if (drawer && state.sections) {
        drawer.renderSections(state.sections);
      } else if (drawer) {
        await drawer.refresh();
      }

      // Let the rest of the theme know the cart changed. Source 'cart-items' stops the drawer
      // from re-fetching itself a second time (we've already rendered the fresh HTML).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENT !== 'undefined') {
        publish(PUB_SUB_EVENT.cartUpdate, { source: 'cart-items', cartData: state });
      }
    } catch (error) {
      this.revert();
      this.setLoading(false);
      this.showError(error.message);
      // In case a two-step swap half-completed, re-sync the drawer with the real cart
      if (drawer && this.dataset.keepProperties === 'true') drawer.refresh();
    }
  }
}

customElements.define('cart-variant-picker', CartVariantPicker);

